// client/src/pages/School_Admin/SchoolSetupandConfiguration/AcademicSessionsPage.js
//
// ACADEMIC YEARS — which years this school runs, and on what dates.
//
// A SCHOOL DOES NOT INVENT A YEAR. The platform defines them and a school
// ADOPTS one. Enrolments, fees and reports all carry the platform's year id, so
// a year a school made up would be one nothing else in the system could name.
//
// WHAT A SCHOOL OWNS is when its own year runs — inside the platform's window,
// never outside it, because dates outside would file enrolments into a year the
// platform says had already ended.
//
// The page this replaced talked to an AcademicSession model that does not
// exist, so every call answered 500 and nothing on it ever worked; it also
// called a /:id/status route that was never written. Both are gone.
//
// A SCHOOL READS THIS PAGE; IT DOES NOT WRITE IT.
//
// Only the SystemAdmin may adopt a year, change its dates or stop using one.
// The reason is the same one that stops a school inventing a year in the first
// place: an academic year is platform-wide state that enrolments, fees, marks
// and report cards are all filed against, and moving its dates — or removing it
// — silently changes what every scoped page in the app shows. That is a
// platform decision, not a per-school one.
//
// So for everyone else the page keeps its whole shape and loses only its verbs:
// the same table, the same overlap warning, the same "which year is now" card,
// with Add, Edit and Remove gone and a line saying who to ask. Hiding the page
// outright would be worse — a school still needs to see which years it runs and
// which one is current, and that is most of what this page is for.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogContent,
  DialogTitle, DialogActions, TextField, Chip, Snackbar, Alert, Stack,
  CircularProgress, Divider, Tooltip, IconButton
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Warning as WarningIcon, Event as EventIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';
import useCurrentUser from '../../../utils/useCurrentUser';

const BASE = '/api/school-setup-and-configuration/academic-sessions';
const day = (v) => (v ? String(v).slice(0, 10) : '');

// Ported by hand from packages/core/src/portal/admin.js — the web client cannot
// import @ameca/core (packages/core is not a workspace), so the phone and the
// desktop keep two copies of these two functions on purpose. Change one, change
// the other: they exist so both clients refuse the same dates for the same
// reasons, and the server refuses them again regardless.

/** What is wrong with a session's dates, or null. */
function sessionProblem({ startDate, endDate, globalStartDate, globalEndDate }) {
  const hasStart = Boolean(startDate);
  const hasEnd = Boolean(endDate);
  if (!hasStart && !hasEnd) return null;          // back to the platform dates
  if (!hasStart) return 'start';
  if (!hasEnd) return 'end';
  if (String(endDate) <= String(startDate)) return 'order';
  if (globalStartDate && String(startDate) < String(globalStartDate)) return 'outside';
  if (globalEndDate && String(endDate) > String(globalEndDate)) return 'outside';
  return null;
}

/**
 * Years that overlap, which is a real problem rather than a style note.
 *
 * Two years covering the same day make "which year is this enrolment in" have
 * two answers, and the winner is whichever the query happened to sort first.
 */
function overlappingSessions(list = []) {
  const rows = [...list]
    .filter((s) => s.startDate && s.endDate)
    .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
  const out = [];
  for (let i = 1; i < rows.length; i += 1) {
    if (String(rows[i].startDate) <= String(rows[i - 1].endDate)) {
      out.push([rows[i - 1], rows[i]]);
    }
  }
  return out;
}

/**
 * Whether this user may change the academic years, or only look at them.
 *
 * Matched on the NORMALIZED role the way every other gate in the client is —
 * `roleNormalized` first, `role` as the fallback — and slugged, because the
 * same role reaches the client as 'SystemAdmin', 'System Admin' and
 * 'system_admin' depending on which endpoint answered.
 */
function isSystemAdmin(user) {
  const slug = String(user?.roleNormalized || user?.role || '')
    .toLowerCase().replace(/[^a-z]/g, '');
  return slug === 'systemadmin' || slug === 'superadmin';
}

const AcademicSessionsPage = () => {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  // Until /api/auth/me answers, `user` is null and this reads false — which is
  // the safe direction: the page renders read-only for an instant and then
  // grows its buttons, rather than offering an action that will be refused.
  const canManage = isSystemAdmin(user);

  const [sessions, setSessions] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [edit, setEdit] = useState(null);       // adopted year being adjusted
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const say = useCallback(
    (message, severity = 'success') => setSnackbar({ open: true, message, severity }),
    []
  );
  // The server's own words first: it knows WHICH year clashed and by how much,
  // and a generic "could not save" throws that away.
  const complain = useCallback(
    (e, fallback) => say(e?.data?.error || e?.userMessage || e?.message || fallback, 'error'),
    [say]
  );

  const load = useCallback(async () => {
    try {
      const res = await fetchJson(BASE, { method: 'GET' });
      setSessions(Array.isArray(res?.sessions) ? res.sessions : []);
      setAvailable(Array.isArray(res?.available) ? res.available : []);
    } catch (e) {
      complain(e, t('academicSessions.errors.load', {
        defaultValue: 'Could not load the academic years.'
      }));
    } finally {
      setLoading(false);
    }
  }, [t, complain]);

  useEffect(() => { load(); }, [load]);

  const current = sessions.find((s) => s.isCurrent) || null;
  const clashes = overlappingSessions(sessions);
  const clashing = new Set(clashes.flat().map((s) => String(s.id)));
  const problem = edit ? sessionProblem(edit) : null;

  // Guarded here as well as in the UI. The buttons are gone for a school
  // admin, but a dialog left open across a role change is a write nobody
  // clicked for — and the server refuses it anyway, so this only decides
  // whether they see the refusal.
  const save = async () => {
    if (!canManage || !edit || problem) return;
    setBusy(true);
    try {
      await fetchJson(`${BASE}/${edit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate: edit.startDate || null,
          endDate: edit.endDate || null,
          isCurrent: Boolean(edit.isCurrent)
        })
      });
      setEdit(null);
      say(t('academicSessions.saved', { defaultValue: 'Academic year saved.' }));
      load();
    } catch (e) {
      complain(e, t('academicSessions.errors.save', {
        defaultValue: 'Could not save the academic year.'
      }));
    } finally { setBusy(false); }
  };

  // Adopting takes the platform's dates; a school that runs its own adjusts
  // them straight after. Asking up front would make starting next year a form.
  const adopt = async (year) => {
    if (!canManage) return;
    setBusy(true);
    try {
      await fetchJson(BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ globalAcademicYearId: year.id })
      });
      setAdoptOpen(false);
      say(t('academicSessions.adopted', {
        name: year.name, defaultValue: '{{name}} is now available.'
      }));
      load();
    } catch (e) {
      complain(e, t('academicSessions.errors.adopt', {
        defaultValue: 'Could not add that year.'
      }));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!canManage) return;
    setBusy(true);
    try {
      await fetchJson(`${BASE}/${confirm.id}`, { method: 'DELETE' });
      setConfirm(null);
      say(t('academicSessions.deleted', { defaultValue: 'Academic year removed.' }));
      load();
    } catch (e) {
      complain(e, t('academicSessions.errors.delete', {
        defaultValue: 'Could not remove it — enrolments may belong to that year.'
      }));
    } finally { setBusy(false); }
  };

  const openEdit = (s) => canManage && setEdit({
    id: s.id,
    name: s.name,
    startDate: s.isLocal ? day(s.startDate) : '',
    endDate: s.isLocal ? day(s.endDate) : '',
    globalStartDate: day(s.globalStartDate),
    globalEndDate: day(s.globalEndDate),
    isCurrent: Boolean(s.isCurrent)
  });

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between', gap: 1, mb: 1
      }}>
        <Typography variant="h5" fontWeight={700}>
          {t('academicSessions.title', { defaultValue: 'Academic Years' })}
        </Typography>
        {canManage && (
          <Tooltip title={available.length === 0
            ? t('academicSessions.allAdopted', {
              defaultValue: 'Every year the platform has defined is already in use here.'
            })
            : ''}>
            <span>
              <Button variant="contained" startIcon={<AddIcon />}
                disabled={busy || available.length === 0}
                onClick={() => setAdoptOpen(true)}>
                {t('academicSessions.add', { defaultValue: 'Use another year' })}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('academicSessions.purpose', {
          defaultValue: 'The academic years this school runs. Enrolments, fees and reports are all filed against one of them.'
        })}
      </Typography>

      {/* Said once, plainly, rather than as a disabled button per row with a
          tooltip nobody hovers. */}
      {!canManage && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('academicSessions.readOnly', {
            defaultValue: 'These are set by the platform administrator. Enrolments, fees and report cards are all filed against a year, so adding one, moving its dates or removing it is not something a school can do on its own — ask your system administrator.'
          })}
        </Typography>
      )}

      {/* WHICH YEAR IS NOW — the one figure that answers "what is the app
          showing me". A list of ten years does not. */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {t('academicSessions.currentYear', { defaultValue: 'Current year' })}
          </Typography>
          <Typography variant="h5" fontWeight={700}
            color={current ? 'text.primary' : 'error.main'}>
            {current
              ? current.name
              : t('academicSessions.noneCurrent', { defaultValue: 'None chosen' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {current
              ? `${day(current.startDate)} → ${day(current.endDate)}`
              : t('academicSessions.noneCurrentBody', {
                defaultValue: 'Until one is chosen, pages scoped to a year have nothing to show.'
              })}
          </Typography>
        </CardContent>
      </Card>

      {clashes.length > 0 && (
        <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            {t('academicSessions.overlapTitle', {
              defaultValue: 'Two years cover the same days'
            })}
          </Typography>
          {clashes.map(([a, b], i) => (
            <Typography key={i} variant="body2">
              {t('academicSessions.overlapPair', {
                a: a.name, b: b.name, defaultValue: '{{a}} and {{b}} overlap'
              })}
            </Typography>
          ))}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('academicSessions.overlapWhy', {
              defaultValue: 'An enrolment dated on an overlapping day belongs to whichever year the database happens to return first. Adjust your dates.'
            })}
          </Typography>
        </Alert>
      )}

      <Card>
        <CardContent>
          {sessions.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('academicSessions.empty', { defaultValue: 'No years in use' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('academicSessions.emptyBody', {
                  defaultValue: 'Add a year the platform has defined. Every enrolment and payment is filed against one.'
                })}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('academicSessions.col.year', { defaultValue: 'Year' })}</TableCell>
                    <TableCell>{t('academicSessions.col.starts', { defaultValue: 'Starts' })}</TableCell>
                    <TableCell>{t('academicSessions.col.ends', { defaultValue: 'Ends' })}</TableCell>
                    <TableCell>{t('academicSessions.col.dates', { defaultValue: 'Dates' })}</TableCell>
                    {/* The column goes with the buttons. An empty Actions
                        header over an empty column is a promise the page has
                        no intention of keeping. */}
                    {canManage && (
                      <TableCell align="right">
                        {t('academicSessions.col.actions', { defaultValue: 'Actions' })}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                          {s.isCurrent && (
                            <Chip size="small" color="primary"
                              label={t('academicSessions.now', { defaultValue: 'Now' })} />
                          )}
                          {clashing.has(String(s.id)) && (
                            <Chip size="small" color="error" variant="outlined"
                              label={t('academicSessions.overlaps', {
                                defaultValue: 'Overlaps'
                              })} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{day(s.startDate)}</TableCell>
                      <TableCell>{day(s.endDate)}</TableCell>
                      <TableCell>
                        {/* Whose dates these are matters: a school that has not
                            set its own follows the platform, and moves if the
                            platform moves. */}
                        <Typography variant="caption" color="text.secondary">
                          {s.isLocal
                            ? t('academicSessions.ourDates', { defaultValue: 'Our dates' })
                            : t('academicSessions.platformDates', { defaultValue: 'Platform dates' })}
                        </Typography>
                      </TableCell>
                      {canManage && (
                        <TableCell align="right">
                          <IconButton size="small" disabled={busy} onClick={() => openEdit(s)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <Tooltip title={s.isCurrent
                            ? t('academicSessions.cannotRemoveCurrent', {
                              defaultValue: 'This is the current year. Make another current first.'
                            })
                            : ''}>
                            <span>
                              <IconButton size="small" color="error"
                                disabled={busy || Boolean(s.isCurrent)}
                                onClick={() => setConfirm(s)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* -- adjust one year ------------------------------------------------ */}
      <Dialog open={canManage && Boolean(edit)} onClose={() => !busy && setEdit(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edit?.name || ''}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('academicSessions.platformWindow', {
              from: edit?.globalStartDate, to: edit?.globalEndDate,
              defaultValue: 'The platform runs this year from {{from}} to {{to}}. Your own dates go inside that.'
            })}
          </Typography>

          <Stack spacing={2}>
            <TextField type="date" fullWidth
              label={t('academicSessions.start', { defaultValue: 'We start' })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: edit?.globalStartDate, max: edit?.globalEndDate }}
              value={edit?.startDate || ''}
              onChange={(e) => setEdit((p) => {
                const startDate = e.target.value;
                return {
                  ...p,
                  startDate,
                  // An end before the new start is not a year, so it gives way
                  // rather than being left contradicting the start.
                  endDate: p.endDate && startDate && p.endDate <= startDate ? '' : p.endDate
                };
              })} />

            <TextField type="date" fullWidth
              label={t('academicSessions.end', { defaultValue: 'We end' })}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: edit?.startDate || edit?.globalStartDate,
                max: edit?.globalEndDate
              }}
              value={edit?.endDate || ''}
              onChange={(e) => setEdit((p) => ({ ...p, endDate: e.target.value }))} />

            {Boolean(edit?.startDate || edit?.endDate) && (
              <Button size="small" sx={{ alignSelf: 'flex-start' }} disabled={busy}
                onClick={() => setEdit((p) => ({ ...p, startDate: '', endDate: '' }))}>
                {t('academicSessions.usePlatform', {
                  defaultValue: 'Use the platform’s dates'
                })}
              </Button>
            )}

            {problem && (
              <Alert severity="warning">
                {t(`academicSessions.needs.${problem}`, {
                  defaultValue: {
                    start: 'Choose when your year starts.',
                    end: 'Choose when your year ends.',
                    order: 'It must end after it starts.',
                    outside: 'Your dates must fall inside the platform’s year.'
                  }[problem]
                })}
              </Alert>
            )}

            <Divider />

            {/* CURRENT is what every scoped page reads to decide "now". There is
                no un-current: something must always be selected. */}
            {edit?.isCurrent ? (
              <Chip color="primary"
                label={t('academicSessions.isCurrent', {
                  defaultValue: 'This is the year we are in'
                })} />
            ) : (
              <>
                <Button variant="outlined" sx={{ alignSelf: 'flex-start' }} disabled={busy}
                  onClick={() => setEdit((p) => ({ ...p, isCurrent: true }))}>
                  {t('academicSessions.markCurrent', {
                    defaultValue: 'Make this the year we are in'
                  })}
                </Button>
              </>
            )}

            {Boolean(edit?.isCurrent) && Boolean(current)
              && String(current.id) !== String(edit?.id) && (
              <Alert severity="warning">
                {t('academicSessions.currentMoves', {
                  name: current.name,
                  defaultValue: 'Every page scoped to a year will show {{name}} no longer, and this year instead.'
                })}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} disabled={busy}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" onClick={save} disabled={busy || Boolean(problem)}>
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -- adopt another year --------------------------------------------- */}
      <Dialog open={canManage && adoptOpen} onClose={() => !busy && setAdoptOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t('academicSessions.add', { defaultValue: 'Use another year' })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('academicSessions.adoptBody', {
              defaultValue: 'These are the years the platform has defined. Adding one takes its dates; you can set your own afterwards.'
            })}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {available.map((y) => (
                  <TableRow key={y.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{y.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${day(y.startDate)} → ${day(y.endDate)}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" disabled={busy}
                        onClick={() => adopt(y)}>
                        {t('academicSessions.use', { defaultValue: 'Use' })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdoptOpen(false)} disabled={busy}>
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -- stop using one -------------------------------------------------- */}
      <Dialog open={canManage && Boolean(confirm)} onClose={() => !busy && setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t('academicSessions.deleteTitle', { defaultValue: 'Stop using this year' })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {t('academicSessions.deleteBody', {
              name: confirm?.name || '',
              defaultValue: '{{name}} will no longer be available to this school. The year itself stays on the platform, and nothing already recorded is deleted — if anything is enrolled in it, the removal is refused instead.'
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" color="error" onClick={remove} disabled={busy}>
            {t('common.remove', { defaultValue: 'Remove' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AcademicSessionsPage;
